import { ComponentRegistry } from '@zagru/design-system';
import { usePermissions, type ApiClient, type CanScope } from '@zagru/platform';
import { createContext, useContext, useMemo, type ReactElement, type ReactNode } from 'react';
import { type ApiBinding, type Binding, type QueryBinding, type ScreenDefinition } from './definitions';
import { SEMANTIC_QUERY_PATH } from './definitions/constants';
import { fragmentStore as globalFragmentStore, type FragmentLoader, type FragmentStore } from './fragments';
import { interpolate, resolvePath } from './path';

export interface BindingContext {
  /** Screen context (e.g. `{ receipt }`) used by context bindings, interpolation and `visibleWhen`. */
  context: Record<string, unknown>;
  client: ApiClient;
  signal?: AbortSignal;
}

export interface MetadataDataSources {
  /** Performs an `api` binding request (default: GET/POST through the ApiClient with `{path}` interpolation). */
  api: (binding: ApiBinding, ctx: BindingContext) => Promise<unknown>;
  /** Performs a semantic `query` binding (default: `POST /api/v1/semantic/query`). */
  query: (binding: QueryBinding, ctx: BindingContext) => Promise<unknown>;
}

export type ActionHandler = (code: string, payload?: Record<string, unknown>) => void | Promise<void>;
export type ResolveBindingOverride = (binding: Binding, ctx: Omit<BindingContext, 'signal'>) => unknown;
export type PermissionChecker = (permission: string, scope?: CanScope) => boolean;

export interface MetadataContextValue {
  registry: ComponentRegistry;
  /**
   * False when no registry was passed: renderers then load the design system's default registry on first use
   * (C7, `useDefaultRegistry`) and wait for it instead of reporting unknown components.
   */
  registryReady: boolean;
  dataSources: MetadataDataSources;
  /** Synchronous override: a non-`undefined` result replaces the default resolution of a binding. */
  resolveBinding?: ResolveBindingOverride;
  can: PermissionChecker;
  context: Record<string, unknown>;
  /** Statically provided fragments per extension slot id. */
  fragments: Record<string, ScreenDefinition[]>;
  /** Async fragment loader consulted by `ExtensionSlot` (cached in `fragmentStore`). */
  loadFragments?: FragmentLoader;
  fragmentStore: FragmentStore;
  onAction?: ActionHandler;
}

function mapValues(record: Record<string, string>, fn: (value: string) => string): Record<string, string> {
  return Object.fromEntries(Object.entries(record).map(([k, v]) => [k, fn(v)]));
}

export const defaultDataSources: MetadataDataSources = {
  api: async (binding, ctx) => {
    const path = interpolate(binding.path, ctx.context);
    const query = binding.query ? mapValues(binding.query, (v) => interpolate(v, ctx.context)) : undefined;
    const options = { query, signal: ctx.signal };
    return (binding.method ?? 'GET') === 'POST' ? ctx.client.post(path, binding.body, options) : ctx.client.get(path, options);
  },
  query: (binding, ctx) => ctx.client.post(SEMANTIC_QUERY_PATH, binding.query, { signal: ctx.signal }),
};

const MetadataContext = createContext<MetadataContextValue | null>(null);

/** Stands in until a renderer resolves the default registry. */
const emptyRegistry = new ComponentRegistry();

export interface MetadataProviderProps {
  children: ReactNode;
  registry?: ComponentRegistry;
  dataSources?: Partial<MetadataDataSources>;
  resolveBinding?: ResolveBindingOverride;
  /** Permission checker (default: the session grants via `usePermissions`). */
  can?: PermissionChecker;
  /** Merged over the parent provider's context. */
  context?: Record<string, unknown>;
  fragments?: Record<string, ScreenDefinition[]>;
  loadFragments?: FragmentLoader;
  fragmentStore?: FragmentStore;
  onAction?: ActionHandler;
}

/** Supplies registry, data sources, permission checker, screen context and fragments to the renderers. Nests. */
export function MetadataProvider({ children, registry, dataSources, resolveBinding, can, context, fragments, loadFragments, fragmentStore, onAction }: MetadataProviderProps): ReactElement {
  const parent = useContext(MetadataContext);
  const permissions = usePermissions();
  const value = useMemo<MetadataContextValue>(
    () => ({
      registry: registry ?? parent?.registry ?? emptyRegistry,
      registryReady: !!registry || !!parent?.registryReady,
      dataSources: { ...defaultDataSources, ...(parent?.dataSources ?? {}), ...(dataSources ?? {}) },
      resolveBinding: resolveBinding ?? parent?.resolveBinding,
      can: can ?? parent?.can ?? permissions.can,
      context: { ...(parent?.context ?? {}), ...(context ?? {}) },
      fragments: { ...(parent?.fragments ?? {}), ...(fragments ?? {}) },
      loadFragments: loadFragments ?? parent?.loadFragments,
      fragmentStore: fragmentStore ?? parent?.fragmentStore ?? globalFragmentStore,
      onAction: onAction ?? parent?.onAction,
    }),
    [parent, registry, dataSources, resolveBinding, can, permissions.can, context, fragments, loadFragments, fragmentStore, onAction],
  );
  return <MetadataContext.Provider value={value}>{children}</MetadataContext.Provider>;
}

/** Nearest provider value; without a provider, defaults (design-system registry, session permissions, global fragment store). */
export function useMetadata(): MetadataContextValue {
  const ctx = useContext(MetadataContext);
  const permissions = usePermissions();
  return useMemo<MetadataContextValue>(
    () =>
      ctx ?? {
        registry: emptyRegistry,
        registryReady: false,
        dataSources: defaultDataSources,
        can: permissions.can,
        context: {},
        fragments: {},
        fragmentStore: globalFragmentStore,
      },
    [ctx, permissions.can],
  );
}

export { resolvePath };
